import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faArrowLeft,
  faDownload,
  faRefresh,
  faSearch,
  faTimes,
  faLink,
  faUser,
  faWandMagicSparkles,
  faRobot,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import {
  getSettlementBatchUploadHistory,
  getSettlementBatchProcessedRows,
  exportSettlementBatchProcessedRows,
  getSettlementBatchAiSummary,
  getSettlementBatchGeneratedFiles,
} from "../../services/settlementApi";

const SettlementBatchUploadProcessedPage = () => {
  const navigate = useNavigate();
  const { uploadId } = useParams();

  const [uploadInfo, setUploadInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowType, setRowType] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState([]);
  const [generatedLoading, setGeneratedLoading] = useState(false);
  const [generatedFilesOpen, setGeneratedFilesOpen] = useState(false);

  // AI Summary state
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiSummary, setAiSummary] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");

  // Frontend filter state
  const [filterPaymentRef, setFilterPaymentRef] = useState("");
  const [filterCvNo, setFilterCvNo] = useState("");
  const [filterCheckNo, setFilterCheckNo] = useState("");
  const [filterCheckAmount, setFilterCheckAmount] = useState("");
  const [filterCvDate, setFilterCvDate] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDetails, setFilterDetails] = useState("");

  // Pagination (client-side)
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);

  // Fetch upload info
  useEffect(() => {
    const fetchUploadInfo = async () => {
      try {
        const response = await getSettlementBatchUploadHistory({ page: 1, limit: 1000 });
        const upload = response.data.data?.find((u) => u.upload_id === uploadId);
        if (upload) {
          setUploadInfo(upload);
        } else {
          toast.error("Upload not found");
          navigate("/settlement-batch-upload-history");
        }
      } catch (error) {
        toast.error("Failed to fetch upload info");
        console.error(error);
      }
    };
    if (uploadId) fetchUploadInfo();
  }, [uploadId, navigate]);

  // Fetch ALL processed rows (frontend filtering)
  const fetchRows = useCallback(async () => {
    if (!uploadId) return;
    setLoading(true);
    try {
      const response = await getSettlementBatchProcessedRows(uploadId, {
        page: 1,
        limit: 5000,
        type: rowType,
      });
      const data = response.data.data || [];
      setAllRows(data);
      setRows(data);
      setPage(1);
    } catch (error) {
      toast.error("Failed to fetch processed rows");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [uploadId, rowType]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  // Fetch generated settlement PDFs (token-backed public links)
  const fetchGeneratedFiles = useCallback(async () => {
    if (!uploadId) return;
    setGeneratedLoading(true);
    try {
      const response = await getSettlementBatchGeneratedFiles(uploadId);
      setGeneratedFiles(response.data.data || []);
    } catch (error) {
      console.error(error);
      setGeneratedFiles([]);
    } finally {
      setGeneratedLoading(false);
    }
  }, [uploadId]);

  const handleToggleGeneratedFiles = () => {
    const shouldOpen = !generatedFilesOpen;
    setGeneratedFilesOpen(shouldOpen);
    if (shouldOpen && generatedFiles.length === 0 && !generatedLoading) {
      fetchGeneratedFiles();
    }
  };

  function parseDateInput(value) {
    const v = String(value ?? "").trim();
    if (!v) return null;

    let day;
    let month;
    let year;

    let match = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (match) {
      day = Number(match[1]);
      month = Number(match[2]);
      year = Number(match[3]);
    } else {
      const isoDate = v.slice(0, 10);
      match = isoDate.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!match) return null;
      year = Number(match[1]);
      month = Number(match[2]);
      day = Number(match[3]);
    }

    const parsed = new Date(Date.UTC(year, month - 1, day));
    if (
      parsed.getUTCFullYear() !== year ||
      parsed.getUTCMonth() + 1 !== month ||
      parsed.getUTCDate() !== day
    ) {
      return null;
    }

    return { day, month, year, date: parsed };
  }

  function toDisplayDate(value) {
    const parsed = parseDateInput(value);
    if (!parsed) return String(value ?? "").trim();
    const dd = String(parsed.day).padStart(2, "0");
    const mm = String(parsed.month).padStart(2, "0");
    return `${dd}/${mm}/${parsed.year}`;
  }

  function toDisplayDateWords(value) {
    const parsed = parseDateInput(value);
    if (!parsed) return String(value ?? "").trim();
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
      timeZone: "UTC",
    }).format(parsed.date);
  }

  // Frontend filtering
  const filteredRows = useMemo(() => {
    return allRows.filter((row) => {
      const match = (val, filter) =>
        !filter || String(val ?? "").toLowerCase().includes(filter.toLowerCase());
      if (!match(row.payment_ref_no, filterPaymentRef)) return false;
      if (!match(row.cv_no, filterCvNo)) return false;
      if (!match(row.check_no, filterCheckNo)) return false;
      if (!match(row.check_amount, filterCheckAmount)) return false;
      const creditedDateSearchValue = `${row.credited_date ?? ""} ${toDisplayDate(row.credited_date)} ${toDisplayDateWords(row.credited_date)}`;
      if (!match(creditedDateSearchValue, filterCvDate)) return false;
      if (!match(row.result_type, filterStatus)) return false;
      const detailsText = row.error_message || row.processed_by || "";
      if (!match(detailsText, filterDetails)) return false;
      return true;
    });
  }, [allRows, filterPaymentRef, filterCvNo, filterCheckNo, filterCheckAmount, filterCvDate, filterStatus, filterDetails]);

  // Paginated slice of filteredRows
  const paginatedRows = useMemo(() => {
    const start = (page - 1) * perPage;
    return filteredRows.slice(start, start + perPage);
  }, [filteredRows, page, perPage]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / perPage));

  const handleClearFilters = () => {
    setFilterPaymentRef("");
    setFilterCvNo("");
    setFilterCheckNo("");
    setFilterCheckAmount("");
    setFilterCvDate("");
    setFilterStatus("");
    setFilterDetails("");
    setPage(1);
  };

  const hasActiveFilters = filterPaymentRef || filterCvNo || filterCheckNo || filterCheckAmount || filterCvDate || filterStatus || filterDetails;

  // AI Summary handler
  const handleOpenAiSummary = async () => {
    setAiModalOpen(true);
    setAiError("");
    // If already loaded, don't re-fetch
    if (aiSummary) return;
    setAiLoading(true);
    try {
      const response = await getSettlementBatchAiSummary(uploadId);
      setAiSummary(response.data.data);
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to generate AI summary";
      setAiError(msg);
      toast.error(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleRegenerateAiSummary = async () => {
    setAiSummary(null);
    setAiError("");
    setAiLoading(true);
    try {
      // Pass regenerate flag — backend will delete old and regenerate
      const response = await getSettlementBatchAiSummary(uploadId);
      setAiSummary(response.data.data);
    } catch (error) {
      const msg = error.response?.data?.message || "Failed to regenerate AI summary";
      setAiError(msg);
    } finally {
      setAiLoading(false);
    }
  };

  const handleExport = async (exportType) => {
    if (!uploadId) return;
    setExporting(true);
    try {
      const response = await exportSettlementBatchProcessedRows(uploadId, { type: exportType });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `settlement_batch_${uploadId}_${exportType}_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${exportType} rows successfully`);
    } catch (error) {
      toast.error("Export failed");
      console.error(error);
    } finally {
      setExporting(false);
    }
  };

  const getResultBadge = (resultType) => {
    if (resultType === "success") {
      return <span className="px-2 py-1 rounded text-xs font-semibold bg-green-100 text-green-800">Success</span>;
    }
    return <span className="px-2 py-1 rounded text-xs font-semibold bg-red-100 text-red-800">Failed</span>;
  };

  const formatDate = (d) => {
    if (!d) return "—";
    return new Date(d).toLocaleString("en-PH", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const formatCurrency = (value) => {
    const n = Number(value ?? 0);
    if (Number.isNaN(n)) return value || "—";
    return new Intl.NumberFormat("en-PH", { style: "currency", currency: "PHP", minimumFractionDigits: 2 }).format(n);
  };

  const getRowDetails = (row) => {
    if (row.error_message) return row.error_message;
    if (row.processed_by) return `Processed by ${row.processed_by}`;
    return "—";
  };

  if (!uploadInfo) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="text-center py-12"><p className="text-gray-600">Loading upload information...</p></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-full mx-auto">

        {/* Header */}
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate("/settlement-batch-upload-history")}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg flex items-center gap-2 text-sm font-medium"
          >
            <FontAwesomeIcon icon={faArrowLeft} /> Back
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Upload Details</h1>
            <p className="text-gray-600 text-sm mt-0.5">{uploadInfo.original_file_name}</p>
          </div>
          {/* AI Summary Button */}
          <button
            onClick={handleOpenAiSummary}
            disabled={uploadInfo.status === "processing" || uploadInfo.status === "uploaded"}
            className="group relative ml-auto px-5 py-2.5 rounded-xl font-semibold text-sm text-white
              bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600
              hover:from-purple-500 hover:via-indigo-500 hover:to-blue-500
              disabled:from-gray-400 disabled:via-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed
              shadow-lg shadow-indigo-500/25 hover:shadow-indigo-500/40
              transition-all duration-300 ease-out hover:scale-[1.03] active:scale-[0.98]
              flex items-center gap-2.5 overflow-hidden"
          >
            {/* Animated shimmer overlay */}
            <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent
              translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-1000 ease-in-out" />
            {/* Glow pulse ring */}
            <span className="absolute inset-0 rounded-xl opacity-0 group-hover:opacity-100
              transition-opacity duration-500 ring-2 ring-indigo-400/50 ring-offset-2 ring-offset-transparent" />
            <FontAwesomeIcon
              icon={faWandMagicSparkles}
              className="relative z-10 text-base group-hover:rotate-12 transition-transform duration-300"
            />
            <span className="relative z-10">AI Summary</span>
            {/* Sparkle dot */}
            <span className="relative z-10 flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-yellow-300 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-yellow-400" />
            </span>
          </button>
        </div>

        {/* Summary */}
        <div className="bg-white rounded-lg shadow-md p-5 mb-5">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            <div>
              <p className="text-xs text-gray-500 mb-1">Total Rows</p>
              <p className="text-2xl font-bold text-gray-800">{uploadInfo.total_rows || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Success</p>
              <p className="text-2xl font-bold text-green-600">{uploadInfo.success_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Failed</p>
              <p className="text-2xl font-bold text-red-600">{uploadInfo.failed_count || 0}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Status</p>
              <p className={`text-lg font-bold ${uploadInfo.status === "completed" ? "text-green-600" : uploadInfo.status === "failed" ? "text-red-600" : "text-yellow-600"}`}>
                {(uploadInfo.status || "").charAt(0).toUpperCase() + (uploadInfo.status || "").slice(1)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded By</p>
              <p className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <FontAwesomeIcon icon={faUser} className="text-gray-400" />
                {uploadInfo.uploaded_by_name || "—"}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 mb-1">Uploaded</p>
              <p className="text-xs text-gray-700">{formatDate(uploadInfo.created_at)}</p>
            </div>
          </div>

          {uploadInfo.error_message && (
            <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
              <p className="text-sm text-red-800"><strong>Error:</strong> {uploadInfo.error_message}</p>
            </div>
          )}
        </div>

        {/* Row type tabs + export */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              {[
                { key: "all", label: `All Rows (${uploadInfo.total_rows || 0})`, color: "blue" },
                { key: "success", label: `Success (${uploadInfo.success_count || 0})`, color: "green" },
                { key: "failed", label: `Failed (${uploadInfo.failed_count || 0})`, color: "red" },
              ].map(({ key, label, color }) => (
                <button
                  key={key}
                  onClick={() => { setRowType(key); setPage(1); }}
                  className={`px-3 py-2 rounded text-sm font-medium ${
                    rowType === key
                      ? `bg-${color}-600 text-white`
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                  }`}
                >
                  {label}
                </button>
              ))}
              <button
                onClick={fetchRows}
                disabled={loading}
                className="px-3 py-2 bg-gray-200 hover:bg-gray-300 disabled:bg-gray-200 text-gray-700 rounded flex items-center gap-2 text-sm"
              >
                <FontAwesomeIcon icon={faRefresh} className={loading ? "animate-spin" : ""} /> Refresh
              </button>
            </div>

            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => handleExport("all")}
                disabled={exporting || (uploadInfo.total_rows || 0) === 0}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
              >
                <FontAwesomeIcon icon={faDownload} /> Export All
              </button>
              {rowType === "failed" && (
                <button
                  onClick={() => handleExport("failed")}
                  disabled={exporting || (uploadInfo.failed_count || 0) === 0}
                  className="px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg text-sm flex items-center gap-2"
                >
                  <FontAwesomeIcon icon={faDownload} /> Export Failed
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Frontend Filters */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
              Filter Rows (frontend)
            </p>
            {hasActiveFilters && (
              <button
                onClick={handleClearFilters}
                className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1"
              >
                <FontAwesomeIcon icon={faTimes} /> Clear Filters
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Payment Ref No</label>
              <input
                type="text"
                value={filterPaymentRef}
                onChange={(e) => { setFilterPaymentRef(e.target.value); setPage(1); }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">CV No</label>
              <input
                type="text"
                value={filterCvNo}
                onChange={(e) => { setFilterCvNo(e.target.value); setPage(1); }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Check No</label>
              <input
                type="text"
                value={filterCheckNo}
                onChange={(e) => { setFilterCheckNo(e.target.value); setPage(1); }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Check Amount</label>
              <input
                type="text"
                value={filterCheckAmount}
                onChange={(e) => { setFilterCheckAmount(e.target.value); setPage(1); }}
                placeholder="Search..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">CV Date</label>
              <input
                type="text"
                value={filterCvDate}
                onChange={(e) => { setFilterCvDate(e.target.value); setPage(1); }}
                placeholder="DD/MM/YYYY..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setPage(1); }}
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Details (Notes)</label>
              <input
                type="text"
                value={filterDetails}
                onChange={(e) => { setFilterDetails(e.target.value); setPage(1); }}
                placeholder="Error or processor..."
                className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-xs focus:outline-none focus:border-blue-500"
              />
            </div>
          </div>
          {hasActiveFilters && (
            <p className="text-xs text-blue-600 mt-2">
              Showing {filteredRows.length} of {allRows.length} rows (filtered)
            </p>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs lg:text-sm">
              <thead className="bg-gray-100 border-b border-gray-300 sticky top-0">
                <tr>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Row #</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Payment Ref No</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">CV No</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Check No</th>
                  <th className="px-3 py-3 text-right font-semibold text-gray-700">Check Amount</th>
                  <th className="px-3 py-3 text-center font-semibold text-gray-700">CV Date<br/><span className="font-normal text-gray-500">(formerly Credit Date)</span></th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Status</th>
                  <th className="px-3 py-3 text-left font-semibold text-gray-700">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan="8" className="px-3 py-8 text-center text-gray-500">Loading...</td></tr>
                ) : paginatedRows.length === 0 ? (
                  <tr><td colSpan="8" className="px-3 py-8 text-center text-gray-500">No rows found</td></tr>
                ) : (
                  paginatedRows.map((row, idx) => (
                    <tr key={`${row.id}-${idx}`} className="border-b border-gray-200 hover:bg-gray-50">
                      <td className="px-3 py-2.5 text-gray-700 font-medium">{row.row_number}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.payment_ref_no || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.cv_no || "—"}</td>
                      <td className="px-3 py-2.5 text-gray-800">{row.check_no || "—"}</td>
                      <td className="px-3 py-2.5 text-right text-gray-800">{formatCurrency(row.check_amount)}</td>
                      <td className="px-3 py-2.5 text-center text-gray-800">{toDisplayDateWords(row.credited_date) || "—"}</td>
                      <td className="px-3 py-2.5">{getResultBadge(row.result_type)}</td>
                      <td className="px-3 py-2.5 text-xs min-w-[360px]">
                        <textarea
                          readOnly
                          rows={3}
                          value={getRowDetails(row)}
                          className={`w-full min-h-[72px] resize-y rounded-md border px-2.5 py-2 text-xs leading-relaxed focus:outline-none ${
                            row.error_message
                              ? "border-red-200 bg-red-50 text-red-800"
                              : "border-gray-200 bg-gray-50 text-gray-600"
                          }`}
                          title={getRowDetails(row)}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {filteredRows.length === 0 ? 0 : (page - 1) * perPage + 1} to{" "}
              {Math.min(page * perPage, filteredRows.length)} of {filteredRows.length} rows
            </div>
            <div className="flex gap-2 items-center">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleLeft} />
              </button>
              <span className="text-sm font-medium text-gray-700">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-100 disabled:bg-gray-100 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faAngleRight} />
              </button>
            </div>
          </div>
        </div>

        {/* Generated PDF Files */}
        <div className="bg-white rounded-lg shadow-md p-5 mt-5 border border-indigo-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <button
              type="button"
              onClick={handleToggleGeneratedFiles}
              className="flex items-start gap-3 text-left"
            >
              <FontAwesomeIcon
                icon={faAngleRight}
                className={`mt-0.5 text-indigo-600 transition-transform ${generatedFilesOpen ? "rotate-90" : ""}`}
              />
              <span>
                <span className="block text-sm font-bold text-gray-800">Generated Settlement PDF Files</span>
                <span className="block text-xs text-gray-500">
                  Reference files. Public links use share tokens and redirect to short-lived S3 signed URLs.
                </span>
              </span>
            </button>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{generatedFilesOpen ? "Expanded" : "Collapsed by default"}</span>
              {generatedFilesOpen && (
                <button
                  onClick={fetchGeneratedFiles}
                  disabled={generatedLoading}
                  className="px-3 py-1.5 text-xs rounded bg-indigo-100 hover:bg-indigo-200 text-indigo-700 font-semibold"
                >
                  {generatedLoading ? "Refreshing..." : "Refresh Files"}
                </button>
              )}
            </div>
          </div>

          {generatedFilesOpen && (
            <div className="mt-4">
              {generatedLoading ? (
                <p className="text-sm text-gray-500">Loading generated files...</p>
              ) : generatedFiles.length === 0 ? (
                <p className="text-sm text-gray-500">No generated files yet for this upload.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs lg:text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Type</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">File Name</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Generated</th>
                        <th className="px-3 py-2 text-center font-semibold text-gray-700">Downloads</th>
                        <th className="px-3 py-2 text-left font-semibold text-gray-700">Public Link</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatedFiles.map((file) => (
                        <tr key={file.id} className="border-b border-gray-100">
                          <td className="px-3 py-2 text-gray-700 uppercase font-semibold">{file.file_type}</td>
                          <td className="px-3 py-2 text-gray-800">{file.file_name}</td>
                          <td className="px-3 py-2 text-gray-600">{formatDate(file.generated_at)}</td>
                          <td className="px-3 py-2 text-center text-gray-700">{file.download_count || 0}</td>
                          <td className="px-3 py-2">
                            {file.public_share_url ? (
                              <a
                                href={file.public_share_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
                                title="Open public share link"
                              >
                                <FontAwesomeIcon icon={faLink} /> Open
                              </a>
                            ) : (
                              <span className="text-gray-400">Unavailable</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* ── AI Summary Modal ── */}
      {aiModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in"
            onClick={() => setAiModalOpen(false)}
          />
          {/* Modal Panel */}
          <div className="relative w-full max-w-3xl max-h-[85vh] bg-white rounded-2xl shadow-2xl
            animate-slide-up flex flex-col overflow-hidden
            border border-gray-200">

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200
              bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                  <FontAwesomeIcon icon={faRobot} className="text-white text-lg" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Upload Details AI Summary</h2>
                  <p className="text-xs text-indigo-200">
                    {aiSummary?.model_used ? `Model: ${aiSummary.model_used}` : "Powered by AI"}
                    {aiSummary?.source === "cached" && " · Cached"}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setAiModalOpen(false)}
                className="p-2 rounded-lg hover:bg-white/20 text-white transition-colors duration-200"
              >
                <FontAwesomeIcon icon={faXmark} className="text-lg" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {aiLoading && (
                <div className="flex flex-col items-center justify-center py-16 gap-4">
                  {/* AI loading animation */}
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin" />
                    <FontAwesomeIcon icon={faWandMagicSparkles}
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2
                        text-indigo-600 text-xl animate-pulse" />
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-semibold text-gray-700">Generating AI Summary...</p>
                    <p className="text-xs text-gray-500 mt-1">Analyzing upload data with AI. This may take a few seconds.</p>
                  </div>
                  {/* Animated dots */}
                  <div className="flex gap-1.5">
                    <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}

              {aiError && !aiLoading && (
                <div className="flex flex-col items-center justify-center py-12 gap-3">
                  <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center">
                    <FontAwesomeIcon icon={faTimes} className="text-red-500 text-xl" />
                  </div>
                  <p className="text-sm text-red-600 font-medium">{aiError}</p>
                  <button
                    onClick={handleOpenAiSummary}
                    className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg
                      transition-colors duration-200"
                  >
                    Try Again
                  </button>
                </div>
              )}

              {aiSummary && !aiLoading && (
                <div className="prose prose-sm max-w-none
                  prose-headings:text-gray-800 prose-headings:font-bold
                  prose-p:text-gray-700 prose-strong:text-gray-800
                  prose-ul:text-gray-700 prose-li:text-gray-700
                  prose-h2:text-lg prose-h2:mt-5 prose-h2:mb-2
                  prose-h3:text-base prose-h3:mt-4 prose-h3:mb-1">
                  {/* Render markdown as HTML */}
                  <div dangerouslySetInnerHTML={{
                    __html: aiSummary.summary_text
                      ?.replace(/^### (.*$)/gim, '<h3>$1</h3>')
                      .replace(/^## (.*$)/gim, '<h2>$1</h2>')
                      .replace(/^# (.*$)/gim, '<h1>$1</h1>')
                      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
                      .replace(/\*(.*?)\*/gim, '<em>$1</em>')
                      .replace(/^- (.*$)/gim, '<li>$1</li>')
                      .replace(/(<li>.*<\/li>)/s, '<ul>$1</ul>')
                      .replace(/\n{2,}/g, '</p><p>')
                      .replace(/\n/g, '<br/>')
                      || ''
                  }} />
                </div>
              )}
            </div>

            {/* Footer */}
            {aiSummary && !aiLoading && (
              <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
                <div className="text-xs text-gray-500 flex gap-4">
                  {aiSummary.prompt_tokens != null && (
                    <span>Prompt tokens: <strong>{aiSummary.prompt_tokens?.toLocaleString()}</strong></span>
                  )}
                  {aiSummary.completion_tokens != null && (
                    <span>Completion tokens: <strong>{aiSummary.completion_tokens?.toLocaleString()}</strong></span>
                  )}
                  {aiSummary.total_tokens != null && (
                    <span>Total tokens: <strong>{aiSummary.total_tokens?.toLocaleString()}</strong></span>
                  )}
                </div>
                <button
                  onClick={() => setAiModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm rounded-lg
                    transition-colors duration-200 font-medium"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SettlementBatchUploadProcessedPage;
